// src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    // Check if user is logged in on app load
    useEffect(() => {
        const checkAuthStatus = async () => {
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            if (token) {
                try {
                    const response = await axios.get(`${process.env.REACT_APP_API_URL}/user`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    setUser(response.data);
                    setIsLoggedIn(true);
                } catch (error) {
                    localStorage.removeItem('token');
                    sessionStorage.removeItem('token');
                    setIsLoggedIn(false);
                    setUser(null);
                }
            } else {
                setIsLoggedIn(false);
                setUser(null);
            }
            setIsLoading(false);
        };

        checkAuthStatus();
    }, []);

    const login = async (credentials, rememberMe = false) => {
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/auth/login`, credentials);
            
            if (response.data.success) {
                const { token, user: userData } = response.data;
                if (rememberMe) {
                    localStorage.setItem('token', token);
                } else {
                    sessionStorage.setItem('token', token);
                }
                setUser(userData);
                setIsLoggedIn(true);
                return { success: true, token, user: userData };
            }
            
            return { success: false, message: response.data.message };
        } catch (error) {
            return { 
                success: false, 
                message: error.response?.data?.message || error.message 
            };
        }
    };

    // ✅ NEW: Dedicated function for Google Sign-In
    const loginWithGoogle = async (googleData, rememberMe = false) => {
        try {
            console.log('🔐 AuthContext: Processing Google login', googleData);
            
            // Store token based on rememberMe preference
            const storage = rememberMe ? localStorage : sessionStorage;
            storage.setItem('token', googleData.token);
            // Also store in the other storage to ensure availability on reload
            const otherStorage = rememberMe ? sessionStorage : localStorage;
            otherStorage.setItem('token', googleData.token);

            // Persist user
            localStorage.setItem('user', JSON.stringify(googleData.user));
            sessionStorage.setItem('user', JSON.stringify(googleData.user));
            
            // ✅ Update state immediately
            setUser(googleData.user);
            setIsLoggedIn(true);
            
            console.log('✅ AuthContext: State updated', {
                isLoggedIn: true,
                user: googleData.user
            });
            
            return { success: true, token: googleData.token, user: googleData.user };
        } catch (error) {
            console.error('❌ AuthContext: Google login error', error);
            return { 
                success: false, 
                message: error.message 
            };
        }
    };

    const register = async (userData, rememberMe = false) => {
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/auth/register`, userData);
            
            if (response.data.success) {
                if (response.data.requiresVerification) {
                    return { 
                        success: true, 
                        requiresVerification: true,
                        message: response.data.message 
                    };
                }
                
                const { token, user: userData } = response.data;
                if (rememberMe) {
                    localStorage.setItem('token', token);
                } else {
                    sessionStorage.setItem('token', token);
                }
                setUser(userData);
                setIsLoggedIn(true);
                return { success: true, token, user: userData };
            }
            
            return { success: false, message: response.data.message };
        } catch (error) {
            return { 
                success: false, 
                message: error.response?.data?.message || error.message 
            };
        }
    };

    const logout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('username');
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('username');
        setIsLoggedIn(false);
        setUser(null);
    };

    const value = {
        isLoggedIn,
        user,
        isLoading,
        login,
        loginWithGoogle, // ✅ Export the new function
        register,
        logout
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
